import type { SidebarGroup } from "@plerity/dashboard-shell";
import { createShell, PORTAL_URLS, type SettingsTab } from "@plerity/dashboard-shell";
import { ConfirmProvider } from "@plerity/ui-core";
import { BarChart3, Calendar, FileText, LayoutDashboard, Palette, PenLine, Settings } from "lucide-react";
import { ReportsSettingsTabContent } from "./routes/settings/SettingsTabContent";

const sidebarGroups: SidebarGroup[] = [
  {
    title: "Reports",
    defaultOpen: true,
    routes: [
      {
        path: "overview",
        label: "Overview",
        icon: LayoutDashboard,
        loader: () => import("./routes/reports/ReportsOverview"),
      },
      {
        path: "reports",
        label: "Report History",
        icon: FileText,
        loader: () => import("./routes/reports/ReportHistory"),
      },
      {
        path: "reports/:id",
        label: "Report Detail",
        icon: FileText,
        loader: () => import("./routes/reports/ReportDetail"),
        hidden: true,
      },
    ],
  },
  {
    title: "Builder",
    routes: [
      {
        path: "builder",
        label: "Report Builder",
        icon: PenLine,
        loader: () => import("./routes/reports/ReportBuilder"),
      },
      {
        path: "builder/:templateId",
        label: "Edit Template",
        icon: PenLine,
        loader: () => import("./routes/reports/ReportBuilder"),
        hidden: true,
      },
      {
        path: "templates",
        label: "Templates",
        icon: BarChart3,
        loader: () => import("./routes/reports/Templates"),
      },
    ],
  },
  {
    title: "Delivery",
    routes: [
      {
        path: "schedules",
        label: "Schedules",
        icon: Calendar,
        loader: () => import("./routes/reports/Schedules"),
      },
    ],
  },
  {
    title: "Configuration",
    routes: [
      {
        path: "branding",
        label: "Branding",
        icon: Palette,
        loader: () => import("./routes/reports/Branding"),
      },
    ],
  },
];

declare const __CF_PAGES__: boolean;
const basename = typeof __CF_PAGES__ !== "undefined" && __CF_PAGES__ ? "/" : "/apps/reports";

// Reports-specific settings tabs injected into the unified UserSettingsPage.
// Reached via top-right avatar → Settings (auto-mounted at /settings by createShell).
// Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22 (Phase 2d).
const settingsTabs: SettingsTab[] = [
  {
    id: "reports-settings",
    label: "Data Sources",
    icon: Settings,
    content: <ReportsSettingsTabContent />,
  },
];

const Shell = createShell({
  name: "reports",
  displayName: "Reports",
  sidebarGroups,
  defaultRoute: "/overview",
  currentApp: "reports",
  basename,
  settingsRoute: "/settings",
  // @allow-hardcoded — cross-app navigation to portal security settings page
  securityRoute: PORTAL_URLS.SECURITY,
  auth: {
    required: true,
    entitlement: "reports",
  },
  chat: {
    enabled: true,
    features: ["reports", "templates", "schedules", "branding", "history"],
    apiEndpoint: "/api/_assistant/chat",
  },
  testerNotes: {
    enabled: true,
    contextId: "web-reports",
  },
  // Unified Settings page — reached via top-right avatar → Settings.
  // Profile / Appearance / Notifications / Tester Notes / Assistant / Privacy
  // are auto-included by UserSettingsPage; we inject the Reports-specific tab here.
  // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22 (Phase 2d).
  settings: {
    additionalTabs: settingsTabs,
    assistantName: "Reports Assistant",
  },
});

function App() {
  return (
    <ConfirmProvider>
      <Shell />
    </ConfirmProvider>
  );
}

export default App;
